import { createContext, useContext, useEffect, type ReactNode } from 'react';

import { useStoredState } from './useStoredState.js';

export type ThemeChoice = 'light' | 'dark' | 'system';

/**
 * Where the choice is remembered. The inline script in index.html reads the same
 * key to apply the theme before the first paint — change both together.
 */
export const THEME_STORAGE_KEY = 'voice-sales.theme';

const THEMES: readonly ThemeChoice[] = ['light', 'dark', 'system'];

const ThemeContext = createContext<[ThemeChoice, (next: ThemeChoice) => void] | null>(null);

/**
 * The user's theme: light, dark, or whatever the computer is set to.
 *
 * Applied as the `dark` class on <html>, which is what index.css keys the dark
 * tokens off. In "system" mode it also follows the operating system when that
 * changes while the page is open — a laptop switching to dark at sunset.
 *
 * Same behaviour as the Avatar Assistant's `useTheme`. A provider rather than a
 * bare hook because the choice is made in more than one place (the header and,
 * on a phone, the account menu) while the login page needs it applied too, and
 * two independent copies of the state would each keep their own listener —
 * the stale one switching the page back behind the user's back.
 */
export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [stored, setTheme] = useStoredState<ThemeChoice>(THEME_STORAGE_KEY, 'system');
  // Whatever is in storage was written by someone; anything unrecognised is the
  // default rather than a theme nobody can select back out of.
  const theme = THEMES.includes(stored) ? stored : 'system';

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = (): void => {
      const dark = theme === 'dark' || (theme === 'system' && media.matches);
      document.documentElement.classList.toggle('dark', dark);
    };
    apply();
    if (theme !== 'system') return;
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [theme]);

  return <ThemeContext.Provider value={[theme, setTheme]}>{children}</ThemeContext.Provider>;
}

export function useTheme(): [ThemeChoice, (next: ThemeChoice) => void] {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside <ThemeProvider>');
  return value;
}
